import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { COLLECTIONS } from '@painttheworld/shared';
import { db } from '../backend/firebase';
import firebaseBackend, { auth } from '../backend/firebase';
import { useAppSelector } from '../redux/hooks';
import { selectUid } from '../redux/features/auth/slice';
import { track } from '../helpers/analytics';
import { timeAgo } from '../helpers/timeAgo';
import { signInWithGoogle } from '../helpers/googleSignIn';
import { signInWithApple } from '../helpers/appleSignIn';
import { t } from '../i18n';
import './Forum.scss';

const TITLE_MAX = 80;
const BODY_MAX = 2000;
const THREAD_LIMIT = 100;

interface ThreadSummary {
    id: string;
    title: string;
    authorName: string;
    colorHex: string;
    replyCount: number;
    lastReplyAt: { toMillis?: () => number } | null;
}

const Forum = () => {
    const uid = useAppSelector(selectUid);
    const isSignedIn = uid !== null && !!auth.currentUser && !auth.currentUser.isAnonymous;

    const [threads, setThreads] = useState<ThreadSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [composing, setComposing] = useState(false);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        track('forum_viewed');
    }, []);

    const loadThreads = useCallback(async () => {
        try {
            const q = query(
                collection(db, COLLECTIONS.FORUM_THREADS),
                orderBy('lastReplyAt', 'desc'),
                limit(THREAD_LIMIT)
            );
            const snap = await getDocs(q);
            const rows: ThreadSummary[] = snap.docs.map((d) => {
                const data = d.data() as any;
                return {
                    id: d.id,
                    title: data.title ?? t('web.forum.untitled'),
                    authorName: data.authorName ?? t('web.forum.defaultAuthor'),
                    colorHex: data.colorHex ?? '#4CAF50',
                    replyCount: typeof data.replyCount === 'number' ? data.replyCount : 0,
                    lastReplyAt: data.lastReplyAt ?? null,
                };
            });
            setThreads(rows);
        } catch (err) {
            console.error('[Forum] failed to load threads:', err);
            setError(t('web.forum.loadError'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadThreads();
    }, [loadThreads]);

    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    const canSubmit =
        trimmedTitle.length >= 3 &&
        trimmedTitle.length <= TITLE_MAX &&
        trimmedBody.length >= 1 &&
        trimmedBody.length <= BODY_MAX &&
        !submitting;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setError(null);
        try {
            const createThread = httpsCallable<{ title: string; body: string }, { threadId: string }>(
                getFunctions(firebaseBackend),
                'createForumThread'
            );
            await createThread({ title: trimmedTitle, body: trimmedBody });
            track('forum_thread_created');
            setTitle('');
            setBody('');
            setComposing(false);
            setLoading(true);
            await loadThreads();
        } catch (err: any) {
            console.error('[Forum] createForumThread failed:', err);
            setError(err?.message || t('web.forum.createError'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="forum-page">
            <div className="forum-topbar">
                <Link to="/" className="forum-back">{t('web.forum.backToMap')}</Link>
            </div>

            <div className="forum-heading-row">
                <div>
                    <h1 className="forum-title">{t('web.forum.title')}</h1>
                    <p className="forum-subtitle">{t('web.forum.subtitle')}</p>
                </div>
                {isSignedIn && !composing && (
                    <button className="forum-btn" onClick={() => { setComposing(true); setError(null); }}>
                        {t('web.forum.newThread')}
                    </button>
                )}
            </div>

            {error && <p className="forum-error">{error}</p>}

            {!isSignedIn && (
                <div className="forum-signin-cta">
                    <p>{t('web.forum.signInToStart')}</p>
                    <button className="forum-btn" onClick={signInWithGoogle}>{t('web.auth.signInWithGoogle')}</button>
                    <button className="forum-btn forum-btn--apple" onClick={signInWithApple}>{t('web.auth.signInWithApple')}</button>
                </div>
            )}

            {isSignedIn && composing && (
                <div className="forum-compose">
                    <p className="forum-compose-heading">{t('web.forum.newThread')}</p>
                    <input
                        className="forum-input"
                        type="text"
                        placeholder={t('web.forum.titlePlaceholder')}
                        value={title}
                        maxLength={TITLE_MAX + 20}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                    <textarea
                        className="forum-textarea"
                        placeholder={t('web.forum.bodyPlaceholder')}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                    />
                    <div className="forum-compose-footer">
                        <span className={`forum-charcount${trimmedBody.length > BODY_MAX ? ' is-over' : ''}`}>
                            {trimmedBody.length}/{BODY_MAX}
                        </span>
                        <div className="forum-compose-actions">
                            <button
                                className="forum-btn forum-btn--ghost"
                                onClick={() => { setComposing(false); setError(null); }}
                                disabled={submitting}
                            >
                                {t('web.common.cancel')}
                            </button>
                            <button className="forum-btn" onClick={handleSubmit} disabled={!canSubmit}>
                                {submitting ? t('web.forum.posting') : t('web.forum.postThread')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <p className="forum-loading">{t('web.forum.loadingThreads')}</p>
            ) : threads.length === 0 ? (
                <p className="forum-empty">{t('web.forum.noThreads')}</p>
            ) : (
                <ul className="forum-list">
                    {threads.map((th) => (
                        <li key={th.id}>
                            <Link
                                to={`/forum/${th.id}`}
                                className="forum-thread-card"
                                style={{ ['--accent' as any]: th.colorHex }}
                            >
                                <div className="thread-card-body">
                                    <h2 className="thread-card-title">{th.title}</h2>
                                    <div className="thread-card-meta">
                                        <span className="thread-card-author">{th.authorName}</span>
                                    </div>
                                </div>
                                <div className="thread-card-aside">
                                    <span className="thread-card-replies">
                                        {th.replyCount === 1
                                            ? t('web.forum.replyCountOne', { count: th.replyCount })
                                            : t('web.forum.replyCountOther', { count: th.replyCount })}
                                    </span>
                                    {th.lastReplyAt && (
                                        <span className="thread-card-time">{timeAgo(th.lastReplyAt)}</span>
                                    )}
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default Forum;
